import { useMemo } from 'react';
import { useLocation, useParams } from 'react-router-dom';

import { nanoidRegex } from '@mai/types';

// Returns the current team id based on the URL or Local Context
export const useTeamId = () => {
  const { teamId } = useParams();
  return teamId;
};

// Returns the current client id based on the URL or Local Context
export const useClientId = () => {
  const { clientId } = useParams();
  return clientId;
};

// Returns the current matter id based on the URL or Local Context
export const useMatterId = () => {
  const { matterId } = useParams();
  return matterId;
};

// Returns the current workflow id based on the URL or Local Context
export const useWorkflowId = () => {
  const { workflowId } = useParams();
  return workflowId;
};

// Returns the current task id based on the URL or Local Context
export const useTaskId = () => {
  const { taskId } = useParams();
  return taskId;
};

// Returns the current note id based on the URL or Local Context
export const useNoteId = () => {
  const { noteId } = useParams();
  return noteId;
};

// Returns the current document id based on the URL or Local Context
export const useDocumentId = () => {
  const { documentId } = useParams();
  return documentId;
};

// Returns the current conversation id based on the URL or Local Context
export const useConversationId = () => {
  const { conversationId } = useParams();
  return conversationId;
};

// Returns the current contract id based on the URL or Local Context
export const useContractId = () => {
  const { contractId } = useParams();
  return contractId;
};

// Returns the current contact id based on the URL or Local Context
export const useContactId = () => {
  const { contactId } = useParams();
  return contactId;
};

// Returns the current analysis request id based on the URL or Local Context
export const useAnalysisRequestId = () => {
  const { analysisRequestId } = useParams();
  return analysisRequestId;
};

// Returns the current insight request id based on the URL or Local Context
export const useInsightRequestId = () => {
  const { insightRequestId } = useParams();
  return insightRequestId;
};

// Returns the current project id based on the URL or Local Context
export const useProjectId = () => {
  const { projectId } = useParams();
  return projectId;
};

// Returns the current source id based on the URL or Local Context
export const useSourceId = () => {
  const { sourceId } = useParams();
  return sourceId;
};

// Returns the current medical device id based on the URL or Local Context
export const useMedicalDeviceId = () => {
  const { medicalDeviceId } = useParams();
  return medicalDeviceId;
};

// Returns the current research paper id based on the URL or Local Context
export const useResearchPaperId = () => {
  const { researchPaperId } = useParams();
  return researchPaperId;
};

// Returns the current explore request id based on the URL or Local Context
export const useExploreRequestId = () => {
  const { exploreRequestId } = useParams();
  return exploreRequestId;
};

// Returns the current timeline request id based on the URL or Local Context
export const useTimelineRequestId = () => {
  const { timelineRequestId } = useParams();
  return timelineRequestId;
};

// Returns the current research request id based on the URL or Local Context
export const useResearchRequestId = () => {
  const { researchRequestId } = useParams();
  return researchRequestId;
};

// Returns the base path, determined on whether or not the user has focused a team and project or just a team
export const useBasePath = () => {
  const { teamId, projectId } = useParams();
  return teamId && projectId
    ? `/team/${teamId}/project/${projectId}`
    : `/team/${teamId}`;
};

// Generates the list of breadcrumbs for use in the headers
export const useBreadcrumbs = () => {
  const basePath = useBasePath();
  const location = useLocation();

  const breadcrumbs: { label: string; to?: string }[] = useMemo(() => {
    const breadcrumbs: { label: string; to?: string }[] = [];

    const locationParts = location.pathname.replace(basePath, '').split('/');

    for (const [index, part] of locationParts.entries()) {
      const isLast = index === locationParts.length - 1;
      const previousBreadcrumb = breadcrumbs.at(-1);
      if (part === '') {
        breadcrumbs.push({
          label: 'Home',
          to: isLast ? undefined : `${basePath}/home`,
        });
      } else if (part === 'library') {
        breadcrumbs.push({
          label: 'Library',
          to: isLast ? undefined : `${basePath}/library`,
        });
      } else if (part === 'medical-devices') {
        if (previousBreadcrumb?.label === 'Library') {
          breadcrumbs.push({
            label: 'Medical Devices',
            to: isLast ? undefined : `${basePath}/library/medical-devices`,
          });
        } else {
          breadcrumbs.push({
            label: 'Medical Devices',
            to: isLast ? undefined : `${basePath}/medical-devices`,
          });
        }
      } else if (part === 'sources') {
        breadcrumbs.push({
          label: 'Sources',
          to: isLast ? undefined : `${basePath}/library/sources`,
        });
      } else if (part === 'explore-requests') {
        breadcrumbs.push({
          label: 'Explore Requests',
          to: isLast ? undefined : `${basePath}/explore-requests`,
        });
      } else if (nanoidRegex.test(part)) {
        const prefix = previousBreadcrumb?.to
          ? `${previousBreadcrumb.to}/`
          : `${basePath}/`;
        breadcrumbs.push({
          label: 'Details',
          to: isLast ? undefined : `${prefix}${part}`,
        });
      }
    }

    return breadcrumbs;
  }, [basePath, location.pathname]);

  return breadcrumbs;
};
